import { API_CONFIG } from '../config';
import type { ApiResponse } from '../types';

/**
 * Réponse du health check
 */
export interface HealthCheckResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  version?: string;
  services?: {
    database?: 'up' | 'down';
    cache?: 'up' | 'down';
    ai?: 'up' | 'down';
  };
}

/**
 * URL de base de l'API avec le préfixe /accounting/api/v1
 * Conforme à la documentation API
 */
const getApiBaseUrl = (): string => {
  return API_CONFIG.baseUrl;
};

/**
 * API Health Check - Endpoint conforme à la documentation
 * Utilisé pour vérifier la disponibilité du backend
 */
export const healthApi = {
  /**
   * Vérifie la santé du backend
   * Cet endpoint ne nécessite pas de Bearer token
   * @see GET /health (sur /accounting/api/v1/health)
   */
  check: async (): Promise<ApiResponse<HealthCheckResponse>> => {
    const baseUrl = getApiBaseUrl();
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

      const response = await fetch(`${baseUrl}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        return {
          success: false,
          error: `Health check failed: HTTP ${response.status}`
        };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          success: false,
          error: 'Health check timeout - serveur non disponible'
        };
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Impossible de joindre le serveur'
      };
    }
  },

  /**
   * Vérifie simplement si le serveur répond (ping)
   * Retourne true si le serveur est accessible, false sinon
   */
  ping: async (): Promise<boolean> => {
    const result = await healthApi.check();
    return result.success && result.data?.status === 'healthy';
  }
};
